import { useRef, useState } from 'react'
import {
  getOpticalMeasureDistanceRange,
  type FaceData,
  type OpticalMeasureFramingDistanceProfileSettings,
} from '../../index'
import type { MeasurementFlowPhase } from '../types'
import { deriveGuidancePhase } from './deriveGuidancePhase'
import {
  BUFFER_SIZE,
  EYE_OPEN_THRESHOLD,
  SAMPLE_DURATION_MS,
  STABLE_FOR_MS,
  VARIANCE_THRESHOLD,
} from './constants'

type MeasurementOptions<Sample, Result> = {
  distanceProfile: OpticalMeasureFramingDistanceProfileSettings
  extractSample: (incoming: FaceData) => Sample | null
  variance: (samples: Sample[]) => number
  average: (samples: Sample[]) => Result
  /**
   * Fired once at the completion transition with the averaged result. The
   * flow owns the reviewed result (mirroring the SH flow, which reads its
   * result from the native freeze event) so both flows share one shape.
   */
  onComplete: (result: Result) => void
  bufferSize?: number
  varianceThreshold?: number
  stableForMs?: number
  sampleDurationMs?: number
}

export function useStableMeasurement<Sample, Result>({
  distanceProfile,
  extractSample,
  variance,
  average,
  onComplete,
  bufferSize = BUFFER_SIZE,
  varianceThreshold = VARIANCE_THRESHOLD,
  stableForMs = STABLE_FOR_MS,
  sampleDurationMs = SAMPLE_DURATION_MS,
}: MeasurementOptions<Sample, Result>) {
  const distanceRange = getOpticalMeasureDistanceRange(
    distanceProfile,
    'guidance'
  )
  const [phase, setPhase] = useState<MeasurementFlowPhase>('idle')
  const [progress, setProgress] = useState(0)

  const activeRef = useRef(false)
  const samplesRef = useRef<Sample[]>([])
  const bufferRef = useRef<Sample[]>([])
  const stableStartRef = useRef<number | null>(null)
  const sampleStartRef = useRef<number | null>(null)
  const lastProgressRef = useRef(0)
  const pausedAtRef = useRef<number | null>(null)

  const resetStability = () => {
    stableStartRef.current = null
    bufferRef.current = []
    // Drops a pending pause: every caller of this restarts or tears down the
    // measurement, so there is no window left to resume into.
    pausedAtRef.current = null
  }

  const start = () => {
    activeRef.current = true
    samplesRef.current = []
    lastProgressRef.current = 0
    sampleStartRef.current = null
    resetStability()
    setProgress(0)
    setPhase('detecting')
  }

  const stop = () => {
    activeRef.current = false
    sampleStartRef.current = null
    resetStability()
    setPhase('idle')
    setProgress(0)
  }

  const clear = () => {
    activeRef.current = false
    samplesRef.current = []
    sampleStartRef.current = null
    resetStability()
    setProgress(0)
    setPhase('idle')
  }

  /**
   * Stops consuming frames while keeping every accumulated sample, the phase
   * and the progress. A completed (or already stopped) measurement stays
   * untouched so `resume` cannot revive it.
   */
  const pause = () => {
    if (!activeRef.current) return
    activeRef.current = false
    pausedAtRef.current = Date.now()
  }

  const resume = () => {
    const pausedAt = pausedAtRef.current
    if (pausedAt === null) return
    pausedAtRef.current = null

    // The stability and sampling windows are wall-clock based, so their origins
    // are shifted by the paused duration — otherwise the time spent reading the
    // instructions would count as holding still and complete the measurement
    // from pre-pause samples.
    const pausedMs = Date.now() - pausedAt
    if (stableStartRef.current !== null) stableStartRef.current += pausedMs
    if (sampleStartRef.current !== null) sampleStartRef.current += pausedMs

    activeRef.current = true
  }

  const handleFaceData = (incoming: FaceData) => {
    if (!activeRef.current) return

    const now = Date.now()

    const guidancePhase = deriveGuidancePhase(incoming, {
      distanceRange,
      eyeOpenThreshold: EYE_OPEN_THRESHOLD,
    })
    if (guidancePhase) {
      resetStability()
      sampleStartRef.current = null
      setPhase(guidancePhase)
      return
    }

    const sample = extractSample(incoming)
    if (sample === null) {
      resetStability()
      sampleStartRef.current = null
      return
    }

    bufferRef.current = [...bufferRef.current.slice(-(bufferSize - 1)), sample]

    const isStable =
      bufferRef.current.length >= bufferSize &&
      variance(bufferRef.current) < varianceThreshold

    if (sampleStartRef.current !== null) {
      samplesRef.current.push(sample)

      if (!isStable) {
        sampleStartRef.current = null
        samplesRef.current = []
        resetStability()
        setProgress(0)
        setPhase('stabilising')
        return
      }

      const elapsed = now - sampleStartRef.current
      const nextProgress = Math.min(elapsed / sampleDurationMs, 1)

      if (nextProgress - lastProgressRef.current > 0.02 || nextProgress >= 1) {
        lastProgressRef.current = nextProgress
        setProgress(nextProgress)
      }

      if (elapsed >= sampleDurationMs) {
        activeRef.current = false
        sampleStartRef.current = null
        onComplete(average(samplesRef.current))
        setPhase('complete')
      }
      return
    }

    if (!isStable) {
      stableStartRef.current = null
      setPhase('stabilising')
      return
    }

    if (!stableStartRef.current) {
      stableStartRef.current = now
    }

    if (now - stableStartRef.current >= stableForMs) {
      sampleStartRef.current = now
      samplesRef.current = []
      setPhase('sampling')
      return
    }

    setPhase('stabilising')
  }

  return { phase, progress, start, stop, clear, pause, resume, handleFaceData }
}
